import React from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { Field, FieldArray, reduxForm, type WrappedFieldArrayProps } from "redux-form";

import { useGetAdditionalAlignersPatientsQuery } from "~/api";
import { SOArch } from "~/common/constants";
import { getLastCorrection, isPatient } from "~/common/patient";
import { Minus } from "~/common/redesign-v1/icons";
import {
  AdditionalAlignersElastics,
  ReduxFormInput,
  ReduxFormLabelledRadio,
  ReduxFormStageSelect,
} from "~/components/additional_aligners/additional_aligners_page";
import { Button } from "~/components/ui/button";
import { IconButton } from "~/components/ui/icon-button";
import { Label } from "~/components/ui/label";
import { LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { selectInstructions } from "~/slices/instructions";
import { selectAdditionalAlignersFormValues } from "~/slices/redux-form";
import { type RootState, useAppSelector } from "~/store";
import type { Loading } from "~/types/common";

export function PatientNewAdditionalAligners() {
  const selectedPatientId = useAppSelector((state) => selectInstructions(state).patient_id);
  const patientsQuery = useGetAdditionalAlignersPatientsQuery();
  const patients = patientsQuery.data;
  const selectedPatient = patients?.find((patient) => patient.id == selectedPatientId) ?? null;

  return <PatientAdditionalAlignersWithReduxFormInitialized maxStage={selectedPatient?.steps} />;
}

export function PatientUpdateAdditionalAligners({ maxStage }: { maxStage: number }) {
  return <PatientAdditionalAlignersWithReduxFormInitialized maxStage={maxStage} />;
}

function PatientAdditionalAligners({ maxStage }: { maxStage: Loading<number> }) {
  return <FieldArray name="aligners" component={AdditionalAlignersForm} maxStage={maxStage} />;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const PatientAdditionalAlignersWithReduxForm = reduxForm<{}, { maxStage: Loading<number> }>({
  form: "additional_aligners",
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(PatientAdditionalAligners);

const PatientAdditionalAlignersWithReduxFormInitialized = connect(({ patient }: RootState) => {
  let aligners = [formInitialValues];

  if (isPatient(patient)) {
    const lastCorrection = getLastCorrection(patient);

    if (lastCorrection.additional_aligners) {
      aligners = lastCorrection.additional_aligners.map((aligner) => ({
        id: aligner.id,
        stage: aligner.option.stage,
        amount: aligner.option.amount as number,
        treat_arch_id: aligner.option.treat_arch_id,
        elastics_left: aligner.option.elastics_left,
        elastics_right: aligner.option.elastics_right,
      }));
    }
  }

  return { initialValues: { aligners } };
})(PatientAdditionalAlignersWithReduxForm);

export type TAligner = {
  /**
   * Present on update, when aligners service is already created and doctor is updating fields.
   */
  id?: number;
  stage: number | null;
  amount: "" | number;
  treat_arch_id: SOArch | null;
  elastics_left: number | null;
  elastics_right: number | null;
};
export type TAlignersFormValues = { aligners: TAligner[] };

const formInitialValues: TAligner = {
  stage: null,
  amount: "",
  treat_arch_id: null,
  elastics_left: null,
  elastics_right: null,
};

export function getAdditionalAlignersAmount(aligners: TAligner[]): number {
  const ARCHES_PRICE_COEFF = {
    [SOArch.BOTH]: 2,
    [SOArch.UPPER]: 1,
    [SOArch.LOWER]: 1,
  };

  return aligners.reduce((total, aligner) => {
    if (aligner.amount == "" || aligner.treat_arch_id == null) {
      return total;
    }
    return total + aligner.amount * ARCHES_PRICE_COEFF[aligner.treat_arch_id];
  }, 0);
}

function AdditionalAlignersForm({
  fields,
  maxStage,
}: WrappedFieldArrayProps & { maxStage: Loading<number> }) {
  const formValues = useAppSelector(selectAdditionalAlignersFormValues);

  return (
    <div>
      {fields.map((field, i) => {
        const selectedStages = formValues.aligners
          .filter((_, index) => index != i) // Skip current aligner
          .map((aligner) => aligner.stage)
          .filter((stage) => stage != null);

        return (
          <div key={i} className="portlet-body">
            <div className="form-group">
              <Label
                id={`validation-aligners-${i}-stage`}
                htmlFor="stage"
                required
                icon={
                  fields.length > 1 ? (
                    <IconButton
                      srOnlyText={<FormattedMessage id="additional_aligners.stage.delete" />}
                      onClick={() => fields.remove(i)}
                    >
                      {/* The minus mirrors the add-row action without implying destructive deletion. Red is
                          also the form's ERROR colour, so spending it on an ordinary row control weakens it.
                          Same `Minus` the CT-links stepper next to it already uses (`addLinks.tsx`). */}
                      <Minus className="tw-size-4 tw-text-foreground" aria-hidden="true" />
                    </IconButton>
                  ) : null
                }
              >
                <FormattedMessage id="STAGE" />
              </Label>

              <Field
                name={`${field}stage`}
                component={ReduxFormStageSelect}
                normalize={(v: string) => (v >= "0" ? Number(v) : null)}
                maxStage={maxStage}
                disabled={maxStage == undefined}
                excludeStages={selectedStages}
              />

              <p className="tw-mt-1.5 tw-text-xs tw-italic tw-text-gray-500 sm:tw-text-[13px]">
                {maxStage ? undefined : (
                  <FormattedMessage id="additional_aligners.stage.description" />
                )}
              </p>
            </div>

            <LabelledRadioGroup
              className="form-group"
              label={<FormattedMessage id="SELECT_TREAT_ARCHES" />}
              labelProps={{ id: `validation-aligners-${i}-treat_arch_id` }}
              required
            >
              <Field
                name={`${field}treat_arch_id`}
                component={ReduxFormLabelledRadio}
                label={
                  <>
                    <FormattedMessage id="TA_BOTH" />
                    &nbsp;&nbsp;
                    <FormattedMessage id="additional.aligners.both.comment" />
                  </>
                }
                radioValue={SOArch.BOTH}
                normalize={Number}
              />

              <Field
                name={`${field}treat_arch_id`}
                component={ReduxFormLabelledRadio}
                label={<FormattedMessage id="TA_UPPER" />}
                radioValue={SOArch.UPPER}
                normalize={Number}
              />

              <Field
                name={`${field}treat_arch_id`}
                component={ReduxFormLabelledRadio}
                label={<FormattedMessage id="TA_LOWER" />}
                radioValue={SOArch.LOWER}
                normalize={Number}
              />
            </LabelledRadioGroup>

            <div className="form-group">
              <Label id={`validation-aligners-${i}-amount`} htmlFor="amount" required>
                <FormattedMessage id="AMOUNT" />
              </Label>

              {formValues.aligners[i]?.treat_arch_id == SOArch.BOTH ? (
                <div className="row">
                  <div className="col-md-4">
                    <Label htmlFor="amount_upper" semibold>
                      <FormattedMessage id="UPPER_ARCH" />
                    </Label>

                    <Field
                      name={`${field}amount`}
                      component={ReduxFormInput}
                      normalize={(v: string) => (v >= "1" ? Number(v) : null)}
                      id="amount_upper"
                      type="number"
                      className="tw-max-w-[240px]"
                      min={1}
                    />
                  </div>

                  <div className="col-md-4">
                    <Label htmlFor="amount_lower" semibold>
                      <FormattedMessage id="LOWER_ARCH" />
                    </Label>

                    <Field
                      name={`${field}amount`}
                      component={ReduxFormInput}
                      normalize={(v: string) => (v >= "1" ? Number(v) : null)}
                      id="amount_lower"
                      type="number"
                      className="tw-max-w-[240px]"
                      min={1}
                    />
                  </div>
                </div>
              ) : (
                <Field
                  name={`${field}amount`}
                  component={ReduxFormInput}
                  normalize={(v: string) => (v >= "1" ? Number(v) : null)}
                  id="amount"
                  type="number"
                  className="tw-max-w-[240px]"
                  min={1}
                />
              )}
            </div>

            <AdditionalAlignersElastics field={field} />
          </div>
        );
      })}

      {/* `id` is a styling hook for the redesign form. It re-skins this legacy button by
          id in `redesign-v1/patient-form/patient-form.css`, the same way it already does
          `#submit-pacient-btn` / `#save-pacient-btn`. */}
      <Button
        id="add-stage-btn"
        variant="secondary"
        style={{ marginBottom: 15, marginTop: 10 }}
        onClick={(e) => {
          e.preventDefault();
          fields.push(formInitialValues);
        }}
      >
        <FormattedMessage id="pat.additional.aligners.add.stage" />
      </Button>
    </div>
  );
}
